<template>
  <section
    ref="dialogElement"
    :class="[position, `align-${align}`, theme]"
    class="dialog outlined"
  >
    <div v-if="showControls" class="controls">
      <button
        type="button"
        class="button-close"
        aria-label="close"
        @click="close"
      >
        <icon name="closeSmall" size="2.4rem" />
      </button>
    </div>
    <header v-if="slots.header || iconHeader">
      <icon v-if="iconHeader" class="icon" :name="iconHeader" />
      <slot name="header" />
    </header>
    <div v-if="scrollable" class="scrollable">
      <div class="dialog-content">
        <slot />
      </div>
    </div>
    <div v-else class="dialog-content">
      <slot />
    </div>
    <footer v-if="slots.footer">
      <slot name="footer" />
    </footer>
  </section>
</template>
<script lang="ts" setup>
const slots = useSlots();
interface Props {
  closeClickOutside?: boolean;
  showControls?: boolean;
  iconHeader?: string | null;
  position?: 'center' | 'top-right';
  align?: 'left' | 'center';
  closeTimer?: number;
  theme?: 'neutral-50' | 'secondary-dark';
  scrollable?: boolean;
}
const props = defineProps({
  closeClickOutside: { type: Boolean, default: true },
  showControls: { type: Boolean },
  iconHeader: { default: null },
  position: null,
  align: { default: 'left' },
  closeTimer: { default: -1 },
  theme: { default: 'neutral-50' },
  scrollable: { type: Boolean, default: false }
});

const dialogElement = ref<HTMLElement>(null);

const emit = defineEmits(["close"]);

const { close } = useDialogControls(dialogElement, props, emit);

const cssoverflow = computed(() => (props.scrollable ? 'auto' : 'hidden'));

useMathJax(dialogElement, (el) => {
  el.classList.add('show');
});
</script>
<style lang="scss" scoped>
.dialog {
  display: flex;
  position: relative;
  flex-direction: column;
  align-items: center;
  align-self: center;
  justify-content: center;
  min-width: 28rem;
  max-width: calc(100vw - 2rem);
  max-height: calc(100vh - var(--row_gap) - var(--row_gap));
  max-height: calc(100dvh - var(--row_gap) - var(--row_gap));
  padding: 1.8rem;
  box-shadow: rgb(231 227 255 / 50%) 0 0.1rem 0.8rem 0,
    rgb(231 227 255 / 5%) 0 0 0 0.6rem;
  justify-self: center;
  gap: 1.6rem;
  @include stroke('m', 'm');

  /* Contenu ouvert */

  @include above('m') {
    max-width: 44rem;
  }

  @include above('l') {
    // max-height: calc(
    //   100vh - var(--header_height) - var(--row_gap) - var(--row_gap)
    // );
    // max-height: calc(
    //   100dvh - var(--header_height) - var(--row_gap) - var(--row_gap)
    // );
    max-height: calc(100vh - var(--header_height));
    max-height: calc(100dvh - var(--header_height));
    &.top-right {
      align-self: start;
      justify-self: end;
    }
  }

  &.show {
    .scrollable {
      opacity: 1;
    }
  }
}

.dialog-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 0;
  word-break: break-word;
  white-space: pre-line;
  gap: var(--gap_m);
}

.scrollable {
  width: calc(100% + 1.8rem);
  min-height: 10rem;
  margin-left: 1.8rem;
  padding-right: 1.8rem;
  overflow-y: v-bind(cssoverflow);
  transition: opacity 300ms $ease-out-quad;
  opacity: 0;
  &::-webkit-scrollbar {
    width: 0.5rem;
  }
  &::-webkit-scrollbar-thumb,
  &::-webkit-scrollbar-track {
    border-radius: 0.25rem;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--color_neutral_100);
  }

  &::-webkit-scrollbar-track {
    background: var(--color_neutral_60);
  }
}

footer {
  width: 100%;
}
header {
  display: flex;
  align-items: center;
  justify-content: left;
  width: 100%;
  text-align: left;
  @include font('m', '900');

  gap: 1rem;

  .icon {
    flex: 0 0 auto;
  }

  :deep(strong) {
    color: var(--color_secondary);
    font-size: var(--font_size);
    font-weight: var(--font_weight);
    line-height: var(--line_height);
  }
}

.align-center {
  header {
    justify-content: center;
  }
  .dialog-content {
    text-align: center;
  }
}

.controls {
  display: flex;
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  flex-direction: row-reverse;
  width: 100%;
  pointer-events: none;
}
.button-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.8rem;
  height: 1.8rem;
  overflow: hidden;
  transform: translate(0, 0);
  transition: transform 500ms $ease-out-quad, box-shadow 200ms $ease-in-quad;
  border-radius: 50%;
  background-color: var(--color_error);
  color: var(--color_neutral_25);
  pointer-events: all;
  &:hover,
  &:focus {
    transform: rotate(180deg);
  }
}
</style>
